import * as React from "react"
import { cn } from "@verlo/shared/lib/utils"

/**
 * A segmented control: one choice among a few, changing what's shown or how something is
 * done (a view, a period, card vs cash) — never where you are (that's the nav) and never
 * an action (that's a button).
 *
 * Same look as `TabsList` / `TabsTrigger`, so a segmented control and a content tab strip
 * read as the same thing: a slate track, the chosen segment raised in white. No icons —
 * two or three words is the label. `size="lg"` for touch dialogs; `stretch` fills the row.
 *
 *   <Segmented value={view} onChange={setView} options={[{ value: 'list', label: 'List' }, …]} />
 */
const Segmented = React.forwardRef(({ value, onChange, options, className, size = "md", stretch = false, ...props }, ref) => (
  <div
    ref={ref}
    role="group"
    className={cn(
      "inline-flex items-center rounded-lg bg-secondary p-1 text-ink-soft",
      size === "lg" ? "h-11" : "h-9",
      stretch && "flex w-full",
      className,
    )}
    {...props}
  >
    {options.map((o) => {
      const active = o.value === value
      return (
        <button
          key={o.value}
          type="button"
          aria-pressed={active}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 font-medium transition-all",
            size === "lg" ? "h-9 text-[15px]" : "h-7 text-sm",
            stretch && "flex-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo disabled:pointer-events-none disabled:opacity-50",
            active ? "bg-white text-ink shadow-sm" : "hover:text-ink",
          )}
          data-testid={o.testId}
        >
          {o.label}
        </button>
      )
    })}
  </div>
))
Segmented.displayName = "Segmented"

/**
 * An option chip: a value chosen in a form (a size, a modifier, +30 minutes, a weekday).
 * Outlined; the chosen one takes a navy border and tint, never a solid fill — solid navy
 * is the nav's and the primary button's. `OptionChips` lays a set out with wrapping.
 */
const OptionChip = React.forwardRef(({ selected = false, className, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-pressed={selected}
    className={cn(
      "inline-flex h-9 items-center justify-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verlo disabled:pointer-events-none disabled:opacity-50",
      selected
        ? "border-verlo bg-verlo-tint text-verlo font-semibold ring-1 ring-inset ring-verlo"
        : "border-hairline bg-white text-ink-soft hover:border-ink-faint",
      className,
    )}
    {...props}
  >
    {children}
  </button>
))
OptionChip.displayName = "OptionChip"

const OptionChips = ({ value, onChange, options, multiple = false, className, chipClassName }) => {
  const isOn = (v) => (multiple ? (value || []).includes(v) : value === v)
  const pick = (v) => {
    if (!multiple) return onChange(v)
    const cur = value || []
    onChange(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v])
  }
  return (
    <div className={cn("flex flex-wrap gap-2", className)} role="group">
      {options.map((o) => (
        <OptionChip key={o.value} selected={isOn(o.value)} onClick={() => pick(o.value)} className={chipClassName} data-testid={o.testId} disabled={o.disabled}>
          {o.label}
        </OptionChip>
      ))}
    </div>
  )
}

export { Segmented, OptionChip, OptionChips }
